"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useSeerRating = void 0;
const react_1 = require("react");
const useRequestGeneration_1 = require("./useRequestGeneration");
const queries_1 = require("../graphql/queries");
const graphqlCache_1 = require("../utils/graphqlCache");
const stableValue_1 = require("../utils/stableValue");
function useSeerRating() {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [lastHubId, setLastHubId] = (0, react_1.useState)(null);
    const [lastGroupId, setLastGroupId] = (0, react_1.useState)(undefined);
    const beginRequest = (0, useRequestGeneration_1.useRequestGeneration)();
    // Background polls must not flip `loading` — consumers gate spinners/overlays
    // on it, and a 30 s flicker on unchanged data is what that produced.
    const hasData = (0, react_1.useRef)(false);
    const fetchSeerRating = (0, react_1.useCallback)(async (hubId, groupId) => {
        // Switching HVAC groups mid-fetch must not let the old group's slower
        // response overwrite the new group's SEER data.
        const isCurrent = beginRequest();
        if (!hasData.current)
            setLoading(true);
        setError(null);
        setLastHubId(hubId);
        setLastGroupId(groupId);
        try {
            const variables = { hubId };
            if (groupId) {
                variables.groupId = groupId;
            }
            const response = await (0, graphqlCache_1.cachedGraphql)({
                query: queries_1.getSeerRating,
                variables,
            });
            if (!isCurrent())
                return;
            const seerRatingData = response.data?.getSeerRating;
            if (seerRatingData) {
                hasData.current = true;
                setData((prev) => (0, stableValue_1.stable)(prev, seerRatingData));
            }
            else {
                setError('No SEER rating data found for this hub');
            }
        }
        catch (err) {
            console.error('Error fetching SEER rating:', err);
            if (!isCurrent())
                return;
            setError(err instanceof Error ? err.message : 'Failed to fetch SEER rating');
            setData(null);
        }
        finally {
            if (isCurrent())
                setLoading(false);
        }
    }, []);
    const refetch = (0, react_1.useCallback)(async () => {
        if (lastHubId) {
            await fetchSeerRating(lastHubId, lastGroupId);
        }
    }, [lastHubId, lastGroupId, fetchSeerRating]);
    return {
        data,
        loading,
        error,
        fetchSeerRating,
        refetch,
    };
}
exports.useSeerRating = useSeerRating;
exports.default = useSeerRating;
